import * as AvatarPrimitive from '@radix-ui/react-avatar';
import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';

const sizeMap = {
  xs: 'h-6 w-6 text-caption',
  sm: 'h-8 w-8 text-caption',
  md: 'h-10 w-10 text-body',
  lg: 'h-12 w-12 text-body-lg',
} as const;

export interface AvatarProps extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: keyof typeof sizeMap;
  status?: 'online' | 'offline' | 'away';
}

export const Avatar = forwardRef<ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size = 'md', status, children, ...props }, ref) => (
    <span className="relative inline-block">
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          'relative flex shrink-0 overflow-hidden rounded-full',
          sizeMap[size],
          className,
        )}
        {...props}
      >
        {children}
      </AvatarPrimitive.Root>
      {status ? (
        <span
          className={cn(
            'absolute bottom-0 right-0 h-2 w-2 rounded-full ring-2 ring-bg-surface',
            status === 'online' && 'bg-success',
            status === 'offline' && 'bg-text-muted',
            status === 'away' && 'bg-warning',
          )}
          aria-label={status}
        />
      ) : null}
    </span>
  ),
);
Avatar.displayName = 'Avatar';

/**
 * True for a value the browser can load as-is: an absolute http(s) URL, a data: or blob: URL.
 * Anything else that reaches an avatar — notably a Supabase Storage object path such as
 * `<uid>/avatar.jpg`, which is what profiles.avatar_url holds — would be fetched RELATIVE to the
 * current route and come back as the SPA's index.html, so it must be resolved first.
 */
export function isDirectImageSrc(src: string | null | undefined): src is string {
  return typeof src === 'string' && /^(https?:\/\/|data:|blob:)/i.test(src.trim());
}

/** The hook every AvatarImage calls to turn its `src` into something loadable. */
export type UseAvatarSrc = (src: string | null | undefined) => string | undefined;

/** Without a provider: direct URLs pass through, anything else renders the fallback. */
const usePassThroughAvatarSrc: UseAvatarSrc = (src) => (isDirectImageSrc(src) ? src.trim() : undefined);

const AvatarSrcContext = createContext<UseAvatarSrc>(usePassThroughAvatarSrc);

/**
 * Lets the app teach every AvatarImage how to resolve a non-URL src (e.g. sign a private
 * storage path). `useResolve` is called as a hook inside AvatarImage, so it must be a stable
 * hook function — pass a module-level hook, never an inline closure.
 */
export function AvatarSrcProvider({ useResolve, children }: { useResolve: UseAvatarSrc; children: ReactNode }) {
  return <AvatarSrcContext.Provider value={useResolve}>{children}</AvatarSrcContext.Provider>;
}

/** The same resolution AvatarImage uses, for a raw <img> that cannot be an AvatarImage. */
export function useAvatarSrc(src: string | null | undefined): string | undefined {
  const useResolve = useContext(AvatarSrcContext);
  return useResolve(src);
}

export const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, ...props }, ref) => {
  const resolved = useAvatarSrc(src);
  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn('aspect-square h-full w-full object-cover', className)}
      src={resolved}
      {...props}
    />
  );
});
AvatarImage.displayName = 'AvatarImage';

export const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center bg-accent-200 text-accent-700 font-semibold',
      'dark:bg-accent-800 dark:text-accent-200',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = 'AvatarFallback';

export function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}
